import { useTranslation } from 'react-i18next'

import { cn } from '@/lib/utils'

/**
 * The "value or not-shared placeholder" renderer (PX-W6.4, spec §6.5) — the
 * ONLY way a rate, cost or margin figure reaches JSX.
 *
 * Redaction is STRUCTURAL on the wire: a field the server withholds is ABSENT
 * (Jackson `NON_NULL`), never `null`, never `0`. So the contract here is
 * presence-checked on `undefined` and nothing else:
 *  - `value !== undefined` → the formatted server figure, verbatim;
 *  - `value === undefined` → the i18n placeholder `commercial.common.notShared`
 *    on a `data-redacted="true"` marker.
 *
 * There is deliberately NO fallback-value prop: a caller able to hand this
 * component a "default" would be able to invent a figure the server never
 * sent. `0` renders as `0` only when the server actually returned `0`.
 *
 * @param value   - The projection's field (`number | undefined`).
 * @param format  - The display formatter (e.g. `(n) => formatMoney(n, 'EUR')`);
 *   defaults to plain digits.
 * @param className - Optional extra classes on the rendered span.
 */
export function ProjectionValue({
  value,
  format,
  className
}: {
  value: number | undefined
  format?: (n: number) => string
  className?: string
}) {
  const { t } = useTranslation('commercial')
  if (value === undefined) {
    return (
      <span
        data-redacted="true"
        className={cn('text-muted-foreground italic', className)}
      >
        {t('common.notShared')}
      </span>
    )
  }
  return (
    <span data-redacted="false" className={cn('tabular-nums', className)}>
      {format !== undefined ? format(value) : String(value)}
    </span>
  )
}
